import React, { useEffect, useState, useRef } from 'react';
import _, { set } from 'lodash';
import { connect, useDispatch, useSelector } from 'react-redux';
import {  Link as LinkAnchor, NavLink } from 'react-router-dom';
import { createSelector } from 'reselect';
import cx from 'classnames';

import { Urls, CampaignStatus, CampaignTypes, ModalTypes, FileType, ConfirmType } from 'constants/constants';
import { didPropChange } from 'utils/props';
import { yup } from 'utils/forms';
import { move } from 'utils/utils';
import { getVideoDuration, getTypeFromUrl } from 'utils/media'; 

import { selectCurrentUser } from 'store/models/User';
import { getAllTags, getTag, selectCurrentAccount } from 'store/models/Account';
import Link, { selectLinksByIds } from 'store/models/Link';
import Campaign, { selectCampaign, getLinkSettings } from 'store/models/Campaign';
import Overlay, { selectDefaultOverlay, selectOverlays } from 'store/models/Overlay';
import Channel, { selectChannels } from 'store/models/Channel';
import Brand, { selectBrand } from 'store/models/Brand';
import Scene, { selectScenesByIds } from 'store/models/Scene';
import RouteState, { currentPath } from 'store/RouteState';
import UIState from 'store/UIState';

import { ContentCenter, Header, Title, Breadcrumbs } from 'components/Content';
import { ScrollToTop } from 'components/ScrollToTop';
import * as Icons from 'components/Svg';
import ActionBar from 'components/ActionBar';
import BodyTag from 'components/BodyTag';
import ChannelCard from 'components/ChannelCard';
import { Card, CardHead, CardTitle, CardActions, CardItem, CardInputItem, ItemLabel, ItemInput, ItemActions } from 'components/Card';
import { withForm, Form, } from 'components/forms/Form';
import { ErrorList } from 'components/forms/Error';
import Label from 'components/forms/Label';
import { Button, ButtonList, SaveButton, CancelButton, AddButton, DeleteIconButton } from 'components/Button';
import { TextInput, NumberInput } from 'components/forms/TextInput';
import { SwitchInput } from 'components/forms/SwitchInput';
import { RadioListInput, RadioItem } from 'components/forms/RadioListInput';
import { TagList, ChannelTag, SceneTag, ChannelGroupTag } from 'components/Tag';
import { SidePanelOver, SidePanelHeader, SidePanelTitle, SidePanelBody } from 'components/SidePanel';
import { SceneListRow, ReorderableSceneList } from 'components/SceneItem';
import EmptyItem from 'components/EmptyItem';

import SceneLibrary from 'components/SceneLibrary';
import SceneSettings from 'components/SceneSettings';
import { LinkTable } from 'components/LinkItem';
import LinkLibrary from 'components/LinkLibrary';



// ======================
// HELPERS
// ====================== 

const SCHEMA = {
  name: {
    validate: yup
      .string()
      .nullable()
      .trim()  
      .required('Campaign Name is Required')
      .min(3)
      .max(256),
    default: ''
  },
  status: {
    default: 'active'
  },
  priority: {
    default: 50
  },
  type: {
    default: CampaignTypes.ALL
  },
  overlayId: {
    default: null,
  },
  chatFrequency: {
    validate: yup.number().nullable().min(0),
    default: null,
    transformIn: value => { 
      return value === null 
        ? null 
        : msToMinutes(value); 
    }, // Convert milliseconds to minutes
    transformOut: v => v |> parseInt |> # * 60 * 1000, // Convert minutes to milliseconds
  }
}

const DEFAULT_PRIORITY = 50;

// Convert milliseconds to minutes for display
const msToMinutes = ms => ms === null ? null : Math.round(ms / (60 * 1000));


// ======================
// STORE
// ====================== 
const selector = createSelector(
  selectCurrentAccount,
  selectCurrentUser,
  (state, props) => props.campaignId === 'create',
  (state, props) => selectCampaign(state, props.campaignId),
  (state, props) => selectBrand(state, props.brandId),
  selectDefaultOverlay,
  selectOverlays,
  selectChannels,
  (state, props) => selectLinksByIds(state,
    props.campaignId
      |> selectCampaign(state, #)
      |> #?.links
      |> _.map(#, 'linkId')),
  (state, props) => selectScenesByIds(state,
    props.campaignId
      |> selectCampaign(state, #)
      |> #?.scenes
      |> _.map(#, 'sceneId')),
  (account, user, isCreate, campaign, brand, overlay, overlays, channels, links, scenes) => ({ 
    account,
    user,
    isCreate,
    campaign, 
    brand,
    channels,
    links,
    linkSettings: _.keyBy(campaign?.links, 'linkId'),
    scenes,
    defaultSceneSettings: overlay?.settings,
    defaultOverlayId: overlay?._id,
    overlays,
  }),
)



// ======================
// COMPONENT
// ====================== 
const CampaignEditScreen = ({ values, errors, edits, validateForm, validate, setSchema, setDefaults, setErrors, onInputChange, match }) => {
  let {campaignId, brandId} = match?.params;
  let { account, user, isCreate, campaign, brand, channels, links, linkSettings, scenes, overlays, defaultOverlayId, defaultSceneSettings } = useSelector(state => selector(state, { campaignId, brandId }));
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSceneLibrary, setShowSceneLibrary] = useState(false);
  const [showLinkLibrary, setShowLinkLibrary] = useState(false);
  const [showChannelTags, setShowChannelTags] = useState(false);
  const [selectedSceneId, setSelectedSceneId] = useState(null);
  const [selectedSceneSettings, setSelectedSceneSettings] = useState({});
  const [newCampaign, setNewCampaign] = useState(null);
  const [step, setStep] = useState(isCreate ? 1 : null);
  const isDefaultSet = useRef(false);

  let title = step
    ? `${brand?.name}: Create Campaign Step ${step}`
    : `${brand?.name}: Edit Campaign`;
  let tags = getAllTags(account);
  let hasTargetedTags = Boolean(campaign?.tags?.length);
  let selectedScene = _.find(scenes, { _id: selectedSceneId });
  let showAssets = step !== 1;
  let showInfo = step !== 2;
  let baseSettings = _.find(overlays, { _id: values.overlayId })?.settings;
  
  useEffect(() => {
    (async () => {
      await setSchema(SCHEMA);
      setCampaignDefaults();

      setLoading(true);
      if(!isCreate && campaignId) {
        await fetchCampaignData(campaignId);
        await fetchData(campaignId);
      }
      setLoading(false);
    })();
  }, [])
  
  useEffect(() => {
    if(!campaign || isDefaultSet.current)
      return;
    setCampaignDefaults()
  }, [campaign]);

  useEffect(() => {
    if(!campaign || !overlays || !defaultOverlayId)
      return;
    setDefaults({ overlayId: values.overlayId ?? campaign.overlayId ?? defaultOverlayId });
    
  }, [campaign, defaultOverlayId, overlays]);

  const setCampaignDefaults = () => {
    if(!campaign) 
        return;
    campaign
        |> _.pick(#, _.keys(SCHEMA))
        |> _.omit(#, 'overlayId') // handled elsewhere
        |> setDefaults;
    isDefaultSet.current = true;
  }

  const fetchCampaignData = () => Promise.all([
    dispatch(Scene.actions.getSceneLibrary()),
    dispatch(Overlay.actions.fetchOverlay()),
    dispatch(Overlay.actions.fetchOverlaysByAccount()),
    dispatch(Channel.actions.fetchChannels()),
  ]);

  const fetchData = campaignId => Promise.all([
    dispatch(Brand.actions.fetchBrands()),
    dispatch(Campaign.actions.fetchCampaign(campaignId)),
  ]);

  const navigate = (...vars) => dispatch(RouteState.actions.navigate(...vars))

  const onCreateNew = async () => {
    let isValid = await validateForm();
    if(!isValid)
      return;

    try {
      setSaving(true);
      let results = await dispatch(Campaign.actions.createCampaign({
        ...values,
        brandId: brand._id,
      }));
      let newCampaign = results?.body?.campaigns?.[0];
      navigate(Urls.CAMPAIGN_NEW_ROUTE, { 
        campaignId: newCampaign._id,
        brandId: newCampaign.brandId,
      })
      setSaving(false);
      setNewCampaign(newCampaign)
      setStep(2)
      
    } catch(err) {
      setErrors({ server: err.message });
    } finally {
      setSaving(false);
    }
  }

  const onEditSave = async () => {
    let values = await validate();
    if(!values)
      return;

    try {
      setSaving(true);
      await dispatch(Campaign.actions.updateCampaign(campaign._id, values));
      navigate(Urls.CAMPAIGNS);
    } catch(err) {
      setErrors({ server: err.message });
    } finally {
      setSaving(false);
    }
  }

  const onDeleteCampaign = async () => {
    dispatch(UIState.actions.showModal(ModalTypes.CONFIRM, { 
      title: "Delete This Campaign?",
      subtitle: "This will permenantly remove the campaign, but your stats will remain",
      confirmText: "Delete",
      confirmType: ConfirmType.DELETE,
      onConfirm: async () => {
        await dispatch(Campaign.actions.deleteCampaign(campaignId));
        navigate(Urls.CAMPAIGNS);
      },
      onCancel: () => {}
    }));
  }
  const onCancel = () => navigate(Urls.CAMPAIGNS)

  const onResetPriority = (e) => {
    e.preventDefault();
    onInputChange(DEFAULT_PRIORITY, 'priority')
  };

  // CHANNELS
  const onRemoveChannel = channelId => dispatch(Campaign.actions.removeChannel(campaignId, channelId))
  const onAddChannelModal = () => dispatch(UIState.actions.showModal(
    ModalTypes.CHANNEL_PICKER, { 
      selectedIds: _.map(campaign?.channels, 'channelId'),
      onSave: onUpdateChannels}))
  const onUpdateChannels = channelIds => dispatch(Campaign.actions.updateChannels(campaignId, channelIds))
  const onAddFilterModal = () => dispatch(UIState.actions.showModal(
    ModalTypes.CHANNEL_PICKER, { 
      selectedIds: _.map(campaign.filters, 'channelId'),
      onSave: onUpdateFilters,
      title: "Select channels to exclude",
      subtitle: "The campaign will serve to every channel EXCEPT these:"
    }
  ))
  const onUpdateFilters = channelIds => dispatch(Campaign.actions.updateFilters(campaignId, channelIds))
  const onRemoveChannelFilter = channelId => {
    let channelIds = campaign
      |> _.map(#.filters, 'channelId')
      |> _.pull(#, channelId);
    dispatch(Campaign.actions.updateFilters(campaignId, channelIds));
  }

  // TAGS
  const onRemoveTag = tagId => dispatch(Campaign.actions.removeTag(campaignId, tagId))
  const onAddTagClick = () => setShowChannelTags(!showChannelTags);
  const onAddTag = tagId => dispatch(Campaign.actions.addTag(campaignId, tagId))

  // SCENES
  const onSceneSelect = sceneId => {
    setSelectedSceneId(sceneId);
    setSelectedSceneSettings(_.find(campaign.scenes, { sceneId })?.settings)
  }
  const onSceneDeselect = () => {
    setSelectedSceneId(null);
    setSelectedSceneSettings(null)
  }
  const onAddScene = async (sceneId, scene) => {
    let res = await dispatch(Campaign.actions.addScene(campaignId, sceneId));

    // If scene URL is a video, update its duration in settings
    if (scene?.url && getTypeFromUrl(scene.url) === FileType.VIDEO) {
      try {
        const duration = await getVideoDuration(scene.url);
        let length = Math.round(duration) * 1000

        let scenes = res?.body?.campaigns?.[0]?.scenes;
        let sceneSettings = _.find(scenes, { sceneId }) ?? {};
        sceneSettings.settings = _.merge(sceneSettings?.settings, { length });
        
        await dispatch(Campaign.actions.updateSceneSettings(campaignId, [sceneSettings]));
      } catch (err) {
        console.warn('Could not get video duration:', err);
      }
    }

    onHideSceneLibrary();
  }
  const onRemoveScene = sceneId => dispatch(Campaign.actions.removeScene(campaignId, sceneId));
  const onSceneOrderChange = async (sceneId, index) => {
    let scenes = campaign.scenes
      |> _.orderBy(#, 'order', 'asc')
      |> move(#, { sceneId }, index)
      |> _.map(#, (v, idx) => v
        |> _.set(#, 'order', idx)
        |> _.pick(#, ['sceneId', 'order']));
    dispatch(Campaign.actions.updateSceneOrder(campaign._id, scenes));
  }
  const onMoveScene = sceneId => dispatch(UIState.actions.showModal(
    ModalTypes.CAMPAIGN_PICKER, 
    { 
      selectedId: campaignId,
      onSave: async campaignId => {
        if(campaignId !== campaignId ) {
          await dispatch(Campaign.actions.migrateScene(sceneId, campaignId));
          await fetchData(campaignId);
          onHideSceneSettings();
        }
      }}
  ))
  
  const onShowSceneLibrary = () => setShowSceneLibrary(true)
  const onHideSceneLibrary = () => setShowSceneLibrary(false)

  const onSceneSettingsChange = (sceneId, settings) => setSelectedSceneSettings(settings);
  const onHideSceneSettings = () => {
    setSelectedSceneId(null);
    setSelectedSceneSettings(null);
  }
  const onSettingsSave = (sceneId, settings) => {
    let scenes = campaign.scenes;
    let sceneSettings = _.find(scenes, { sceneId });
    sceneSettings.settings = _.merge(sceneSettings.settings, settings);
    dispatch(Campaign.actions.updateSceneSettings(campaignId, [sceneSettings]));
    onHideSceneSettings();
  }

  const onSettingsDelete = sceneId => {
    dispatch(Campaign.actions.removeScene(campaignId, sceneId));
    onHideSceneSettings();
  }

  const onSlideshowPreview = () => {
    if(campaign.type === CampaignTypes.CHANNELS) {
      return navigate(Urls.SLIDESHOW_EDIT_ROUTE, { 
        channelId: campaign.channels 
         |> _.first 
         |> #?.channelId,
      }, 
      null, 
      { back: currentPath() })
    }
    navigate(Urls.SLIDESHOW, null, null, { back: currentPath() });
  }

  // LINKS
  const onShowLinkLibrary = () => setShowLinkLibrary(true);
  const onHideLinkLibrary = () => setShowLinkLibrary(false);

  const onLinkAdd = linkId => {
    dispatch(Campaign.actions.addLink(campaignId, linkId));
    onHideLinkLibrary();
  }
  const onLinkRemove = linkId => dispatch(Campaign.actions.removeLink(campaignId, linkId))
  const onLinkEdit = (linkId) => navigate(Urls.LINK_EDIT_ROUTE, { linkId, }, { back: true,});
  const onLinkCreate = () => navigate(Urls.LINK_NEW, null, { 
    back: true, 
    campaign: campaignId 
  });
  const onLinkSettingsChanged = (linkId, value, name) => getLinkSettings(campaign, linkId)
    |> dispatch(Campaign.actions.updateLinkSettings(campaign._id, linkId, { ...#, [name]: value } ))

  const onPrioritySwitchChange = (v,k,e) => {
    if(v) {
      onInputChange(5, 'chatFrequency', e)
    } else {
      onInputChange(null, 'chatFrequency')    
    }
  }
  const onDeleteChatFrequency = (e) => {
    e.preventDefault();
    onInputChange(null, 'chatFrequency')
  };  

  let onSubmit = step === 1 
    ? onCreateNew 
    : onEditSave;

  return (
    <ContentCenter>
      { !_.isEmpty(errors) && <ScrollToTop />}
      <ScrollToTop />
      <BodyTag className="campaign-edit" />
      <Header split={false}>
        <Breadcrumbs items={{
          dashboard: Urls.DASHBOARD,
          brands: Urls.CAMPAIGNS
        }} />
        <Title>{title}</Title>
      </Header>
      <ErrorList errors={errors} />
      
      <Form onSubmit={onSubmit}> 
      { showInfo &&
        <Card>
          <CardHead>
            <CardTitle>Campaign Info</CardTitle>
          </CardHead>
          <CardInputItem>
            <ItemLabel>
              <Label>Name</Label>
            </ItemLabel>
            <ItemInput>
              <TextInput
                name="name"
                value={values.name ?? ''}
                focused={step === 1}
                helper="Enter a name you'll recognize this campaign by"
                onChange={onInputChange} />
            </ItemInput>
          </CardInputItem>
          <CardInputItem>
            <ItemLabel>
              <Label>Status</Label>
            </ItemLabel>
            <ItemInput>
              <RadioListInput 
                name="status" 
                value={values.status} 
                helper="Turns the campaign on or off. This can be changed at any time. Paused campaigns will not serve slides or chat messages"
                onChange={onInputChange}>
                <RadioItem key={CampaignStatus.ACTIVE} value={CampaignStatus.ACTIVE}>Active</RadioItem>
                <RadioItem key={CampaignStatus.PAUSED} value={CampaignStatus.PAUSED}>Paused</RadioItem>
              </RadioListInput>
            </ItemInput>
          </CardInputItem>
          <CardInputItem>
            <ItemLabel>
              <Label>Ordering</Label>
            </ItemLabel>
            <ItemInput>
              <NumberInput 
                name="priority" 
                value={values.priority} 
                min={0}
                max={100}
                helper="A score between 0-100. This will order this campaign against all your other campaigns. A score of 100 will show these first in any slideshow."
                onChange={onInputChange}>
                <span className="priority-reset">
                  { !_.isEmpty(campaign) && 
                    (values?.priority === DEFAULT_PRIORITY ? <>(Default)</> : <a href={''} onClick={onResetPriority}>Reset</a>)}
                </span>
              </NumberInput>
            </ItemInput>
          </CardInputItem>
        </Card>}

        { showAssets && <>
        <Card>
          <CardHead>
            <CardTitle>Campaign Targeting</CardTitle>
          </CardHead>
          <CardInputItem>
            <ItemLabel>
              <Label>Overlay</Label>
            </ItemLabel>
            <ItemInput className="overlay-input">
              <RadioListInput
                name="overlayId"
                value={values.overlayId}
                onChange={onInputChange}>
                { _.map(overlays, overlay => <RadioItem value={overlay._id} key={overlay._id}>{overlay.name}</RadioItem>)}
              </RadioListInput>
              { _.size(overlays) === 1 && <p className="helper-text">Create additional overlays in your <LinkAnchor to="/account/settings">account settings</LinkAnchor> and target campaigns to them.</p>}
            </ItemInput>
          </CardInputItem>
          <CardInputItem>
            <ItemLabel>
              <Label>Channels</Label>
            </ItemLabel>
            <ItemInput className="channel-input">
              <RadioListInput
                name="type"
                value={values.type}
                onChange={onInputChange}>
                <RadioItem value={CampaignTypes.ALL}>All Channels</RadioItem>
                <RadioItem value={CampaignTypes.TAGS}>Channel Group</RadioItem>
                <RadioItem value={CampaignTypes.CHANNELS}>Specific Channels</RadioItem>
              </RadioListInput>
            
              { values.type === CampaignTypes.ALL && <>
                <div className="campaign-type">This campaign will deliver to all channels in your account.</div>
                { _.size(campaign?.filters) > 0 && 
                <div className="exceptions">
                  <div className="label">Except These:</div>
                  <TagList>
                    {_.map(campaign?.filters, (filter, idx) => {
                      let channel = _.find(channels, { _id: filter.channelId });
                      if(!channel)
                        return null
                      return (
                        <ChannelTag 
                          key={channel._id} 
                          channel={channel} 
                          tagId={channel._id} 
                          onDelete={onRemoveChannelFilter} />)
                      })}
                  </TagList>
                </div>}
                <ItemActions>
                  <AddButton size="s" onClick={onAddFilterModal}>Add Exception</AddButton>
                </ItemActions>
              </>}
              { values.type === CampaignTypes.CHANNELS && <>
                <div className="campaign-type">This campaign will deliver to the following channels:</div>
                { _.isEmpty(campaign?.channels) && <EmptyItem align="left" className="bordered">No channels selected</EmptyItem>}
                { !_.isEmpty(campaign?.channels) &&
                <TagList>
                  {_.map(campaign?.channels, (channelItem, idx) => {
                    let channel = _.find(channels, { _id: channelItem.channelId });
                    if(!channel)
                      return null
                    return (
                      <ChannelTag 
                        key={channel._id} 
                        channel={channel} 
                        tagId={channel._id} 
                        onDelete={onRemoveChannel} />)
                    })}
                </TagList>}
                <ItemActions>
                  <AddButton className="add-channel" size="s" onClick={onAddChannelModal}>Add Channel</AddButton>
                </ItemActions>
              </>}
              {values.type === CampaignTypes.TAGS && <>
                <div className="campaign-type">This campaign will deliver to the following groups of channels:</div>
                { !hasTargetedTags && <EmptyItem align="left" className="bordered">No channels groups selected</EmptyItem>}
                { hasTargetedTags && 
                <TagList>
                  {_.map(campaign?.tags, (tagId, idx) => {
                    let tag = getTag(account, tagId);
                    if(!tag)
                      return null;
                    return <ChannelGroupTag key={tagId} tag={tag} onDelete={onRemoveTag} /> })}
                </TagList>}
                { !showChannelTags && hasTargetedTags &&
                <ItemActions>
                  <AddButton className="add-tag" size="s" onClick={onAddTagClick}>Add Group Tag</AddButton>
                </ItemActions>}
                { (showChannelTags || !hasTargetedTags) && <>
                <TagList className="all-tags">
                  {_.map(tags, tag => {
                    const disabled = Boolean(_.indexOf(campaign?.tags, tag.tagId) > -1)
                    return <ChannelGroupTag key={tag.tagId} tag={tag} disabled={disabled} onAdd={!disabled && onAddTag} onClick={!disabled && onAddTag} />})}
                </TagList>
                <ButtonList size="s">
                  <Button onClick={onAddTagClick} size="s">Done</Button>
                </ButtonList> </>}
              </>}
            </ItemInput>
          </CardInputItem>
        </Card>

        <Card>
          <CardHead>
            <CardTitle>Slides</CardTitle>
            <CardActions>
            { _.size(scenes) > 0 && <>
              <Button 
                size="s" 
                color="light" 
                onClick={onSlideshowPreview}>
                Preview
              </Button>
              <AddButton
                size="s"
                onClick={onShowSceneLibrary}>
                Add Slide
              </AddButton>
            </>}
            </CardActions>
          </CardHead>
          <CardItem>
            <ReorderableSceneList 
              scenes={scenes}
              sceneSettings={_.keyBy(campaign?.scenes, 'sceneId')}
              baseSettings={baseSettings}
              showLength={true}
              showStyle={true}
              wrap={true}
              onOrderChange={onSceneOrderChange}
              onAdd={onShowSceneLibrary}
              onSceneSelect={onSceneSelect} />
          </CardItem>
        </Card>

        <Card>
          <CardHead>
            <CardTitle>Links</CardTitle>
            <CardActions>
              { _.size(links) > 0 &&
              <AddButton size="s" onClick={onShowLinkLibrary}>Add Link</AddButton>}
            </CardActions>
          </CardHead>
          <CardItem>
            <LinkTable 
              links={links}
              linkSettings={linkSettings}
              onAdd={onShowLinkLibrary}
              onDelete={onLinkRemove}
              onEdit={onLinkEdit}
              onSettingsChanged={onLinkSettingsChanged} />
          </CardItem>
        </Card>

        <Card>
          <CardHead>
            <CardTitle>Chat Bot Settings</CardTitle>
          </CardHead>
          <CardInputItem>
            <ItemLabel className="priority-label">
              <Label>Priority Delivery</Label>
            </ItemLabel>
            <ItemInput>
              <SwitchInput
                name="priorityEnabled"
                value={values.chatFrequency > 0}
                onChange={onPrioritySwitchChange} />
              { (values.chatFrequency > 0) && 
              <NumberInput
                className="priority-input"
                name="chatFrequency"
                value={values.chatFrequency}
                min={0}
                postfix="mins"
                onChange={onInputChange}>
                <span></span>
              </NumberInput>}
              <p className="helper-text">
                Priority delivery will <strong>always</strong> post these links, regardless of your other campaigns. 
                Note: This could mean more than one link is posted at a time if they overlap.
              </p>
            </ItemInput>
          </CardInputItem>
        </Card>
        </>}
        
        <ActionBar sideAction={<DeleteIconButton color="fill red" onClick={onDeleteCampaign} />}>
          { step === 1 &&
          <SaveButton loading={saving} submit={true}>Next</SaveButton>}
          { step !== 1 && 
          <SaveButton loading={saving} onClick={onEditSave} />}
          <CancelButton onClick={onCancel} />
        </ActionBar>

        <SidePanelOver 
          show={showSceneLibrary}
          onHide={onHideSceneLibrary}>
          <SidePanelHeader>
            <SidePanelTitle><Icons.SlideshowIcon /> Add New Slide</SidePanelTitle>
          </SidePanelHeader>
          <SidePanelBody>
            <SceneLibrary
              disabledIds={_.map(scenes, '_id')}
              onSceneAdd={onAddScene}
              showDelete={false} />
          </SidePanelBody>
        </SidePanelOver>
        <SidePanelOver
          show={Boolean(selectedSceneId)}
          onHide={onSceneDeselect}>
          <SidePanelHeader>
            <SidePanelTitle><Icons.SettingsIcon /> Slide Settings</SidePanelTitle>
          </SidePanelHeader>
          <SidePanelBody>
            {selectedSceneId &&
              <SceneSettings
                user={user}
                scene={selectedScene}
                campaign={campaign}
                sceneSettings={selectedSceneSettings}
                baseSettings={baseSettings}
                onSettingsChange={onSceneSettingsChange}
                onSave={onSettingsSave}
                onDelete={onSettingsDelete} 
                onMoveScene={onMoveScene} />}
          </SidePanelBody>
        </SidePanelOver>
        <SidePanelOver
          show={showLinkLibrary}
          onHide={onHideLinkLibrary}>
          <SidePanelHeader>
            <SidePanelTitle><Icons.LinkIcon /> Add New Link</SidePanelTitle>
          </SidePanelHeader>
          <SidePanelBody>
            <LinkLibrary
              disableIds={_.map(campaign?.links, 'linkId')}
              onLinkAdd={onLinkAdd}
              onLinkCreate={onLinkCreate} />
          </SidePanelBody>
        </SidePanelOver>
        </Form>
      
    </ContentCenter>)
  
}
export default CampaignEditScreen 
  |> withForm;

